import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import PlotContainer from '../../../containers/PlotContainer';
import {
    useGetValidationSummaryQuery,
    useGetValidationChartQuery,
    useUpdateStageStatusMutation,
    useSendConversationMessageMutation,
} from '../../../redux/api/compassPortal';
import { errMsg } from './bits';

// Data Validation — REAL numbers, per Tobi's 2026-07-20 feedback: an area chart
// of spend by channel over time (backend-supplied Plotly figure, rendered by the
// app's standard PlotContainer) plus the agent's computed spike/drop flags. Each
// flag needs a human call — real spend stays, a data error goes back to be
// corrected. Corrections are sent to Bravo on the project conversation.

// Zoomed context for one flagged movement: that channel only, ±6 weeks,
// red ring on the offending point — so nobody hunts a 2.5-year haystack.
const Spark = ({ w }) => {
    if (!w?.values?.length || w.values.length < 2) return null;
    const W = 220, H = 52, max = Math.max(...w.values, 1);
    const pt = (v, i) => `${(i / (w.values.length - 1)) * (W - 10) + 5},${H - 8 - (v / max) * (H - 18)}`;
    const pts = w.values.map(pt).join(' ');
    const [mx, my] = pt(w.values[w.mark], w.mark).split(',');
    return (
        <svg width={W} height={H} style={{ flex: 'none' }} aria-hidden="true">
            <polyline points={pts} fill="none" stroke="var(--green-color, #01FF85)" strokeWidth="1.6" />
            <circle cx={mx} cy={my} r="4.5" fill="none" stroke="#F06567" strokeWidth="2.2" />
        </svg>
    );
};

const describe = (a) => {
    const f = Number(a.factor) || 0;
    const move = a.kind === 'spike'
        ? `jumps ~${Math.round(a.factor)}×`
        : (Number(a.value) <= 0 || f <= 0) ? 'drops to ~zero (paused?)'
        : `drops to ~1/${Math.round(1 / f)} of`;
    return `${a.channel} spend ${move} its recent level on ${a.date} (${a.baseline.toLocaleString()} → ${a.value.toLocaleString()})`;
};

export default function ValidationWorkspace({ project, stageId, onComplete }) {
    const pid = project.project_id;
    const { user } = useAuth0();
    const [note, setNote] = useState('');
    const [sent, setSent] = useState(null);
    const [gateMsg, setGateMsg] = useState(null);

    const { data: summary, isLoading } = useGetValidationSummaryQuery({ projectId: pid });
    const [sendMessage, { isLoading: busy }] = useSendConversationMessageMutation();
    const [updateStageStatus, { isLoading: completing }] = useUpdateStageStatusMutation();

    const anomalies = summary?.anomalies || [];
    const checks = summary?.checks || [];
    const hasData = (summary?.series || []).length > 0;

    const sendCorrection = async () => {
        const t = note.trim();
        if (!t || busy) return;
        try {
            await sendMessage({
                projectId: pid, text: `[Validation note] ${t}`, stage: stageId,
                author: user?.name || user?.email || undefined,
            }).unwrap();
            setSent('Sent to Bravo — it will apply this and re-check.');
            setNote('');
        } catch (e) { setSent(`⚠ ${errMsg(e)}`); }
    };

    const markValidated = async () => {
        setGateMsg(null);
        try {
            await updateStageStatus({ projectId: pid, stage_id: 'data-validation', status: 'complete' }).unwrap();
            onComplete?.('data-validation', 'data-modelling');
        } catch (e) { setGateMsg(errMsg(e)); }
    };

    if (isLoading) return (
        <div className="cp-empty" style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
            <span className="cp-spin" />
            <span>Bravo is reading the processed data. The first look after new data lands can
                take a minute or two for very large outputs — after that it's instant.</span>
        </div>
    );

    if (!hasData) {
        return (
            <div className="cp-empty">
                Nothing's been processed yet, so there are no numbers to check here.
                Finish Data Processing and the spend checks run the moment the output lands.
            </div>
        );
    }

    return (
        <>
            <div className="cp-toolbar">
                <p className="cp-section-label" style={{ margin: 0 }}>
                    {anomalies.length === 0
                        ? 'Bravo checked the processed spend — nothing looks off'
                        : `Bravo flagged ${anomalies.length} movement(s) that need your call`}
                    {checks.length > 0 && <span> · {checks.length} data check(s) to review</span>}
                    {((summary?.skipped_sources || []).length > 0 || (summary?.warnings || []).length > 0) && (
                        <div style={{ color: 'var(--co-neg)', marginTop: 6, fontSize: 13 }}>
                            {(summary?.skipped_sources || []).map((sk, i) => (
                                <div key={`s${i}`}>⚠ {sk.name} left out of validation — {sk.why} ({sk.size_mb}MB).</div>
                            ))}
                            {(summary?.warnings || []).map((wn, i) => (
                                <div key={`w${i}`}>⚠ {wn.name}: {wn.why} ({wn.size_mb}MB — included in the checks below).</div>
                            ))}
                        </div>
                    )}
                </p>
                <button className="cp-btn cp-btn--primary" disabled={completing} onClick={markValidated}>
                    {completing ? 'Saving…' : 'Looks right — mark validated →'}
                </button>
            </div>
            {gateMsg && <p style={{ color: 'var(--co-neg)', fontSize: 13, marginTop: -4 }}>{gateMsg}</p>}

            {anomalies.length > 0 && (
                <div className="cp-card" style={{ padding: 16, marginBottom: 16 }}>
                    <p className="cp-section-label" style={{ marginTop: 0 }}>What looks off</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '0 0 10px' }}>
                        {anomalies.slice(0, 8).map((a, i) => (
                            <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                                <Spark w={a.window} />
                                <span style={{ fontSize: 14, lineHeight: 1.5 }}>{describe(a)}</span>
                            </div>
                        ))}
                        {anomalies.length > 8 && (
                            <span className="cp-sub">…and {anomalies.length - 8} more — see the red rings on the chart below.</span>
                        )}
                    </div>
                    <p className="cp-sub" style={{ margin: 0 }}>
                        For each one: if it's real spend we keep it; if it looks wrong, tell Bravo what's
                        right and that source goes back to be corrected before we model.
                    </p>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                        <input value={note} onChange={(e) => setNote(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && sendCorrection()}
                            placeholder="e.g. The Meta jump is a real Black Friday burst — keep it."
                            style={{ flex: 1, padding: '10px 12px', borderRadius: 'var(--co-radius-sm)', border: '1px solid rgba(20,22,21,0.2)', font: 'inherit' }} />
                        <button className="cp-btn cp-btn--primary cp-btn--sm" disabled={busy || !note.trim()} onClick={sendCorrection}>
                            Send to Bravo
                        </button>
                    </div>
                    {sent && <p className="cp-sub" style={{ marginTop: 8, color: sent.startsWith('⚠') ? 'var(--co-neg)' : 'var(--co-green-dark)' }}>{sent}</p>}
                </div>
            )}

            {checks.length > 0 && (
                <div className="cp-card" style={{ padding: 16, marginBottom: 16 }}>
                    <p className="cp-section-label" style={{ marginTop: 0 }}>Data checks</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {checks.map((c, i) => (
                            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, lineHeight: 1.5 }}>
                                <span className="cp-check-dot" data-sev={c.severity} />
                                <span>{c.message}</span>
                            </div>
                        ))}
                    </div>
                    <p className="cp-sub" style={{ margin: '10px 0 0' }}>
                        These are automatic integrity checks (gaps, coverage, flat or negative spend, possible
                        double-counts). If one is expected, ignore it; if it's a real data problem, fix the source
                        and re-process before modelling.
                    </p>
                </div>
            )}

            <div className="cp-card" style={{ padding: 8, marginBottom: 16, height: 480, overflow: 'hidden' }}>
                <PlotContainer
                    tooltipTitle="Spend by channel over time — red rings mark movements that look off"
                    useApi={useGetValidationChartQuery}
                    useApiArgs={{ projectId: pid }}
                />
            </div>

        </>
    );
}
